import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

function fmtMonth(year, month) {
  const d = new Date(year, (month || 1) - 1, 1);
  return d.toLocaleDateString("cs-CZ", { month: "long", year: "numeric" });
}

function fmtDateTime(val) {
  if (!val) return "—";
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("cs-CZ");
}

function getUiStatusMeta(uiStatus) {
  switch (uiStatus) {
    case "submitted":
      return {
        label: "Odesláno ke schválení",
        className: "bg-yellow-50 text-yellow-800 border-yellow-200",
      };
    case "approved":
      return {
        label: "Schváleno",
        className: "bg-green-50 text-green-800 border-green-200",
      };
    case "not-submitted":
    default:
      return {
        label: "Neodesláno",
        className: "bg-gray-50 text-gray-700 border-gray-200",
      };
  }
}

function getDeadlineMeta(deadlineStatus) {
  switch (deadlineStatus) {
    case "overdue-submit":
      return {
        label: "Po termínu odeslání",
        className: "bg-orange-50 text-orange-800 border-orange-200",
      };
    case "overdue-approve":
      return {
        label: "Po termínu schválení",
        className: "bg-red-50 text-red-800 border-red-200",
      };
    case "ok":
    default:
      return {
        label: "V termínu",
        className: "bg-green-50 text-green-800 border-green-200",
      };
  }
}

function buildRiskLabel(item) {
  if (!item?.risksEvaluated) return "Nevyhodnoceno";

  const parts = [];
  if (Number(item.criticalRiskCount || 0) > 0) {
    parts.push(`magistr ${item.criticalRiskCount}×`);
  }
  if (Number(item.softRiskCount || 0) > 0) {
    parts.push(`asistent ${item.softRiskCount}×`);
  }

  if (parts.length === 0) return "Bez rizik";
  return parts.join(", ");
}

function SummaryCard({ title, value, tone = "default" }) {
  const toneClass =
    tone === "danger"
      ? "border-red-200 bg-red-50 text-red-800"
      : tone === "warning"
      ? "border-yellow-200 bg-yellow-50 text-yellow-800"
      : tone === "success"
      ? "border-green-200 bg-green-50 text-green-800"
      : "border-gray-200 bg-white text-gray-800";

  return (
    <div className={`rounded-2xl border p-4 shadow-sm ${toneClass}`}>
      <div className="text-sm font-medium opacity-80">{title}</div>
      <div className="mt-2 text-3xl font-bold">{value}</div>
    </div>
  );
}

export default function ShiftOverviewPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const today = new Date();
  const defaultYear = today.getFullYear();
  const defaultMonth = today.getMonth() + 1;

  const [year, setYear] = useState(defaultYear);
  const [month, setMonth] = useState(defaultMonth);
  const [status, setStatus] = useState("");
  const [problematicOnly, setProblematicOnly] = useState(false);
  const [pharmacyCode, setPharmacyCode] = useState("");

  const [meta, setMeta] = useState(null);
  const [summary, setSummary] = useState({
    total: 0,
    notSubmitted: 0,
    submitted: 0,
    approved: 0,
    problematic: 0,
  });
  const [items, setItems] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const isManager = user?.role === "admin" || user?.role === "rm";

  useEffect(() => {
    if (!user?.firma || !isManager) return;

    const load = async () => {
      try {
        setLoading(true);
        setError("");

        const qs = new URLSearchParams({
          firma: user.firma,
          year: String(year),
          month: String(month),
        });

        if (status) qs.set("status", status);
        if (problematicOnly) qs.set("problematicOnly", "1");
        if (pharmacyCode.trim()) qs.set("pharmacyCode", pharmacyCode.trim());

        const res = await fetch(`/api/shifts/overview?${qs.toString()}`, {
          credentials: "include",
        });

        const data = await res.json().catch(() => null);

        if (!res.ok) {
          throw new Error(data?.message || data?.error || `HTTP ${res.status}`);
        }

        setMeta(data?.meta || null);
        setSummary(
          data?.summary || {
            total: 0,
            notSubmitted: 0,
            submitted: 0,
            approved: 0,
            problematic: 0,
          }
        );
        setItems(Array.isArray(data?.items) ? data.items : []);
      } catch (e) {
        console.error("ShiftOverviewPage load error:", e);
        setMeta(null);
        setSummary({
          total: 0,
          notSubmitted: 0,
          submitted: 0,
          approved: 0,
          problematic: 0,
        });
        setItems([]);
        setError("Nepodařilo se načíst přehled směn.");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [user?.firma, isManager, year, month, status, problematicOnly, pharmacyCode]);

  const title = useMemo(() => {
    if (user?.role === "rm") return "Dohled směn – moje pobočky";
    return "Dohled směn";
  }, [user?.role]);

  const openPlan = (item) => {
    if (!item) return;

    const base = user?.role === "admin" ? "/admin/shifts" : "/shifts";

    navigate(
      `${base}?mode=review&pharmacy=${encodeURIComponent(
        item.pharmacyCode
      )}&year=${item.year}&month=${item.month}`
    );
  };

  const openApprovePage = () => {
    navigate("/admin/personal/approve/shifts");
  };

  const openRisks = (item) => {
    if (!item) return;

    navigate(
      `/admin/personal/shifts-risks?pharmacy=${encodeURIComponent(
        item.pharmacyCode
      )}&year=${item.year}&month=${item.month}`
    );
  };

  if (!isManager) {
    return <div className="p-6">Nemáte oprávnění.</div>;
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">{title}</h1>
        <div className="text-sm text-gray-500 mt-1">
          Přehled stavů plánů, termínů a základních rizik za vybraný měsíc.
        </div>
      </div>

      <div className="bg-white border rounded-2xl shadow-sm p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Měsíc</label>
            <select
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm bg-white"
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                <option key={m} value={m}>
                  {String(m).padStart(2, "0")}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Rok</label>
            <input
              type="number"
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Stav</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm bg-white"
            >
              <option value="">Všechny</option>
              <option value="not-submitted">Neodesláno</option>
              <option value="submitted">Odesláno ke schválení</option>
              <option value="approved">Schváleno</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Pobočka</label>
            <input
              type="text"
              value={pharmacyCode}
              onChange={(e) => setPharmacyCode(e.target.value)}
              placeholder="např. 66"
              className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm"
            />
          </div>

          <div className="flex items-end">
            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={problematicOnly}
                onChange={(e) => setProblematicOnly(e.target.checked)}
                className="rounded border-gray-300"
              />
              Jen problémové pobočky
            </label>
          </div>
        </div>

        {meta?.deadlines && (
          <div className="mt-4 text-sm text-gray-600">
            Deadline: odeslat do <span className="font-semibold">{meta.deadlines.submitByDay}.</span> dne,
            schválit do <span className="font-semibold">{meta.deadlines.approveByDay}.</span> dne v měsíci.
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4 mb-6">
        <SummaryCard title="Celkem poboček" value={summary.total} />
        <SummaryCard title="Neodesláno" value={summary.notSubmitted} tone="warning" />
        <SummaryCard title="Odesláno" value={summary.submitted} tone="warning" />
        <SummaryCard title="Schváleno" value={summary.approved} tone="success" />
        <SummaryCard title="Problémové" value={summary.problematic} tone="danger" />
      </div>

      {loading && <div className="text-gray-500">Načítám přehled směn…</div>}

      {!loading && error && (
        <div className="rounded-xl bg-red-50 border border-red-200 text-red-700 p-4">
          {error}
        </div>
      )}

      {!loading && !error && items.length === 0 && (
        <div className="rounded-xl bg-white border p-6 text-gray-500 shadow-sm">
          Pro zadaný filtr nebyly nalezeny žádné záznamy.
        </div>
      )}

      {!loading && !error && items.length > 0 && (
        <div className="overflow-x-auto bg-white rounded-2xl shadow border">
          <table className="min-w-[1100px] w-full text-sm">
            <thead className="bg-gray-50 text-gray-700">
              <tr>
                <th className="text-left px-4 py-3">Pobočka</th>
                <th className="text-left px-4 py-3">Měsíc</th>
                <th className="text-left px-4 py-3">Stav</th>
                <th className="text-left px-4 py-3">Verze</th>
                <th className="text-left px-4 py-3">Poslední změna</th>
                <th className="text-left px-4 py-3">Deadline</th>
                <th className="text-left px-4 py-3">Rizika</th>
                <th className="text-right px-4 py-3">Akce</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const statusMeta = getUiStatusMeta(item.uiStatus);
                const deadlineMeta = getDeadlineMeta(item.deadlineStatus);
                const riskLabel = buildRiskLabel(item);

                return (
                  <tr key={`${item.pharmacyCode}-${item.year}-${item.month}`} className="border-t hover:bg-gray-50">
                    <td className="px-4 py-3 font-semibold text-gray-800">{item.pharmacyCode}</td>

                    <td className="px-4 py-3 text-gray-700">
                      {fmtMonth(item.year, item.month)}
                    </td>

                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold border ${statusMeta.className}`}
                      >
                        {statusMeta.label}
                      </span>
                    </td>

                    <td className="px-4 py-3 text-gray-700">
                      {item.version ? `v${item.version}` : "—"}
                    </td>

                    <td className="px-4 py-3 text-gray-700">{fmtDateTime(item.updatedAt)}</td>

                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold border ${deadlineMeta.className}`}
                      >
                        {deadlineMeta.label}
                      </span>
                    </td>

                    <td className="px-4 py-3">
                    {item.criticalRiskCount > 0 ? (
                        <div className="inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold border bg-red-50 text-red-800 border-red-200 mr-2">
                        magistr {item.criticalRiskCount}×
                        </div>
                    ) : null}

                    {item.softRiskCount > 0 ? (
                        <div className="inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold border bg-orange-50 text-orange-800 border-orange-200 mr-2">
                        asistent {item.softRiskCount}×
                        </div>
                    ) : null}

                    {item.criticalRiskCount === 0 && item.softRiskCount === 0 && (
                        <span className={item.risksEvaluated ? "text-gray-600" : "text-gray-500"}>
                        {riskLabel}
                        </span>
                    )}
                    </td>

                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => openRisks(item)}
                          className="px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition"
                        >
                          Rizika
                        </button>

                        <button
                          onClick={() => openPlan(item)}
                          className="px-3 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition"
                        >
                          Otevřít plán
                        </button>

                        {item.uiStatus === "submitted" && (
                          <button
                            onClick={openApprovePage}
                            className="px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition"
                          >
                            Schvalování
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}